import { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useIntl } from 'react-intl';
import { ButtonUI, CustomThemeProvider, ModalUI, TableListV2, usePortal } from 'sonata-react-components';

import { AudienceProfileModalStyled } from './AudienceProfileModal.styled';

type Props = {
  defaultSelectedItems: Record<string, boolean>;
  formatRequestArgs: (page: number, size: number, filterText: string | null | undefined) => any;
  isModalOpened: boolean;
  onModalClose: () => void;
  onModalSave: (items: Record<string, any>[]) => void;
  parseData: (res: any) => any[];
  tableConfig?: Record<string, any>;
  tableProps?: Record<string, any>;
  theme?: 'light' | 'dark';
  url: string;
};

type State = {
  selectedItems: Record<string, any>[];
};

const AudienceProfileModal = ({
  defaultSelectedItems,
  formatRequestArgs,
  isModalOpened,
  onModalClose,
  onModalSave,
  parseData,
  tableConfig = {},
  tableProps = {},
  theme,
  url
}: Props) => {
  const { formatMessage } = useIntl();
  const modalRef = useRef<any>(null);

  const [{ selectedItems }, setState] = useState<State>({
    selectedItems: []
  });

  useEffect(() => {
    if (isModalOpened) {
      modalRef?.current?.openModal?.();
    } else {
      modalRef?.current?.closeModal?.();
    }
  }, [isModalOpened]);

  const onModalSaveCallback = () => {
    onModalSave?.(selectedItems);
    modalRef?.current?.closeModal?.();
  };

  const handleSelectionChange = (items: Record<string, any>[]) => {
    setState(prevState => ({
      ...prevState,
      selectedItems: items
    }));
  };

  const columns = [
    {
      Header: 'Name',
      accessor: 'name'
    }
  ];

  const targetBody = usePortal('audienceProfileModal');

  return ReactDOM.createPortal(
    <CustomThemeProvider themeContrast={theme}>
      <AudienceProfileModalStyled className="audienceProfileModal">
        <ModalUI
          centerButton={
            <ButtonUI
              onClick={onModalSaveCallback}
              text={formatMessage({
                defaultMessage: 'Save changes',
                id: 'targeting.audienceTargeting.audienceProfile.modal.saveChangesButtonModal'
              })}
            />
          }
          closeModalCallback={onModalClose}
          ref={modalRef}
          title={formatMessage({
            defaultMessage: 'Create a new Audience Profile',
            id: 'targeting.audienceTargeting.audienceProfile.modal.titleModal'
          })}
          width="805px"
        >
          <ButtonUI
            className="audienceProfileModal__back"
            icon="fa-solid fa-arrow-left"
            onClick={onModalClose}
            shape="link"
            text={formatMessage({
              defaultMessage: 'Back to Settings',
              id: 'targeting.audienceTargeting.audienceProfile.modal.back'
            })}
            type="secondary"
          />
          <TableListV2
            className="audienceProfileModal__list"
            columns={columns}
            config={{
              emptyState: {
                icon: 'fal fa-empty-set',
                show: true,
                title: formatMessage({
                  defaultMessage: 'There are no audience IDs',
                  id: 'targeting.audienceTargeting.audienceProfile.modal.emptyState'
                })
              },
              globalFilter: {
                automaticSearch: false,
                className: 'audienceProfileModal__globalFilter',
                onEnter: true,
                position: 'center',
                show: true
              },
              maxHeight: 300,
              onChangeSelection: handleSelectionChange,
              pagination: {
                async: {
                  countData: res => res.totalItems || 0,
                  formatRequestArgs,
                  parseData,
                  url
                },
                lazyLoad: {
                  enabled: true,
                  threshold: 0.75
                },
                pageCount: 1,
                pageIndex: 0,
                pageSize: 10,
                show: true,
                type: 'compact'
              },
              searchEmptyState: {
                icon: 'fal fa-empty-set',
                show: true,
                title: formatMessage({
                  defaultMessage: 'There is no data for the current search.',
                  id: 'targeting.audienceTargeting.audienceProfile.modal.searchEmptyState'
                })
              },
              selectableItem: true,
              type: 'list',
              ...tableConfig
            }}
            defaultSelectedRows={defaultSelectedItems}
            id="audienceProfileModal-tableList"
            isSelectableList
            {...tableProps}
          />
        </ModalUI>
      </AudienceProfileModalStyled>
    </CustomThemeProvider>,
    targetBody
  );
};

export default AudienceProfileModal;
